// src/components/ShareButton.js
// Captures a DOM element (via a ref) as a PNG and shares it through the
// device's native share sheet -- Mail, WhatsApp, WhatsApp Business, etc. --
// using the Web Share API (navigator.share with a file). Supported on iOS
// Safari and Android Chrome, which covers the real-world usage here.
//
// Falls back to simply downloading the PNG when native file sharing isn't
// available (desktop browsers mostly, and any browser missing the API) --
// the user can still attach the downloaded image manually. Never fails
// silently: a real capture/share error surfaces a toast, but the user
// cancelling the share sheet itself is not treated as an error.

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { toPng } from 'html-to-image';

async function captureElement(el) {
  // The on-screen card for these snapshots is deliberately capped at a
  // clean, phone-friendly max-width with overflow:hidden -- good for a
  // compact card on the page itself, but on a wider screen (a laptop, or
  // just a table with more columns than usual) the table's TRUE content
  // can be wider than that cap, and a plain capture just clips to
  // whatever the live element's current box happens to be -- exactly
  // what a laptop capture showing only the first couple of columns,
  // with a chunk of blank space where the page's own layout sat around
  // it, was actually showing. html-to-image lets a capture render its
  // OWN clone at a different size/style than the live element without
  // touching the live page at all, so the fix is to always capture at
  // the element's true full scrollWidth/scrollHeight with the width cap
  // and clipping lifted -- the on-screen card stays exactly as
  // compact as it's meant to look; only what gets exported changes.
  const opts = {
    pixelRatio: 2,
    width: el.scrollWidth,
    height: el.scrollHeight,
    style: { maxWidth: 'none', width: `${el.scrollWidth}px`, overflow: 'visible' },
  };
  // Two passes: html-to-image can occasionally miss custom fonts/late
  // layout on the very first render pass, especially right after a modal
  // opens -- a quick second capture after the first is discarded fixes
  // that without adding a fixed, guessable delay before the first attempt.
  await toPng(el, opts);
  return toPng(el, opts);
}

export default function ShareButton({ targetRef, filename, shareTitle, style }) {
  const [busy, setBusy] = useState(false);

  const handleShare = async () => {
    if (!targetRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await captureElement(targetRef.current);
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `${filename}.png`, { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: shareTitle || filename });
        } catch (shareErr) {
          // AbortError = the user closed the share sheet without picking
          // anything -- that's a normal cancel, not a failure to report.
          if (shareErr?.name !== 'AbortError') throw shareErr;
        }
      } else {
        // No native file sharing available (typically desktop) -- download
        // instead so the image can still be attached manually.
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${filename}.png`; document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success('Image downloaded — attach it in Mail, WhatsApp, etc.');
      }
    } catch (err) {
      console.error('Share failed:', err);
      toast.error('Could not prepare the image to share');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button onClick={handleShare} disabled={busy}
      style={{
        padding: '7px 14px', borderRadius: 8, border: 'none', background: busy ? '#94A3B8' : '#059669',
        color: '#FFFFFF', fontWeight: 700, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer', flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 6,
        ...style,
      }}>
      {busy ? '⏳ Preparing…' : '📤 Share'}
    </button>
  );
}
